import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle, Copy, Loader, Search } from 'lucide-react';
import { messagingAPI } from '../../services/api';

// Read-only message log for one WhatsApp integration, on top of the existing MessageLog history
// endpoint (GET /messaging/history: status + name/mobile search only). Everything else — integration,
// date, delivery state, message-ID search — is filtered here on the rows already returned.
// There is no manual retry: failed sends are retried by the server's own retry job.
const DAY = 86400000;
const RANGES = [['today', 'Today'], ['7', '7 days'], ['30', '30 days'], ['all', 'All']];
const looksLikeId = (q) => q.length >= 8 && !/\s/.test(q) && /[a-z]/i.test(q);

export const inRange = (createdAt, range, now = Date.now()) => {
  if (range === 'all') return true;
  const t = new Date(createdAt).getTime();
  if (range === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return t >= d.getTime(); }
  return t >= now - Number(range) * DAY;
};

// integration: 'kapso' (Official) or 'baileys' (Unofficial: everything that is not Kapso).
export default function MessageLogPanel({ integration }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('');
  const [range, setRange] = useState('7');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 400); return () => clearTimeout(t); }, [q]);

  const idSearch = looksLikeId(query);
  const load = useCallback(() => {
    setError('');
    const params = { channel: 'whatsapp', limit: 1000 };
    if (query && !idSearch) params.search = query;
    messagingAPI.getHistory(params)
      .then(({ data }) => setRows(data.data || []))
      .catch((err) => { setRows([]); setError(err.response?.data?.message || 'Could not load the message log'); });
  }, [query, idSearch]);
  useEffect(() => { load(); }, [load]);

  const mine = useMemo(() => (rows || []).filter((r) => (integration === 'kapso' ? r.provider === 'kapso' : r.provider !== 'kapso')), [rows, integration]);
  const shown = useMemo(() => mine.filter((r) => {
    if (!inRange(r.createdAt, range)) return false;
    if (status === 'sent' || status === 'failed') { if (r.status !== status) return false; }
    else if (status && r.deliveryStatus !== status) return false;
    if (idSearch) { const id = query.toLowerCase(); return String(r.providerMessageId || '').toLowerCase().includes(id) || String(r._id).includes(id); }
    return true;
  }), [mine, range, status, idSearch, query]);

  const failed = shown.filter((r) => r.status === 'failed').length;
  const chips = [['', 'All'], ['sent', 'Sent'], ['failed', 'Failed'], ...(integration === 'kapso' ? [['delivered', 'Delivered'], ['read', 'Read']] : [])];
  const copy = (text) => navigator.clipboard?.writeText(text || '').then(() => toast.success('Copied'), () => toast.error('Could not copy'));

  return (
    <div className="mlog">
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ display: 'grid', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: 12, color: 'var(--text-muted)' }} />
            <input className="form-control" style={{ paddingLeft: 32 }} inputMode="search" placeholder="Customer, mobile or message ID"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="mlog-chips">
            {chips.map(([k, label]) => <button key={k} type="button" className={status === k ? 'mlog-chip mlog-chip-on' : 'mlog-chip'} onClick={() => setStatus(k)}>{label}</button>)}
          </div>
          <div className="mlog-chips">
            {RANGES.map(([k, label]) => <button key={k} type="button" className={range === k ? 'mlog-chip mlog-chip-on' : 'mlog-chip'} onClick={() => setRange(k)}>{label}</button>)}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
            {rows === null ? 'Loading…' : `${shown.length} message${shown.length === 1 ? '' : 's'} · ${shown.length - failed} ok · ${failed} failed`}
            {mine.length >= 1000 && ' · latest 1000 loaded'}
          </div>
        </div>
      </div>

      {error && <div className="card card-body" style={{ color: 'var(--danger, #dc2626)', marginBottom: 12 }}>{error}</div>}
      {rows === null ? <div style={{ textAlign: 'center', padding: 30 }}><Loader size={20} /></div> : (
        <div className="card">
          {shown.length === 0 ? <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: '.85rem' }}>No messages match.</div> : shown.slice(0, 200).map((r) => {
            const isOpen = open === r._id;
            return (
              <div key={r._id} className="mlog-row" onClick={() => setOpen(isOpen ? null : r._id)}>
                {r.status === 'failed' ? <XCircle size={16} color="var(--danger, #dc2626)" style={{ flexShrink: 0, marginTop: 2 }} /> : <CheckCircle2 size={16} color="var(--c-0ca30c, #0ca30c)" style={{ flexShrink: 0, marginTop: 2 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', fontSize: '.85rem', fontWeight: 600 }}>
                    <span style={{ overflowWrap: 'anywhere' }}>{r.customerName || r.mobile}</span>
                    <span style={{ fontWeight: 400, fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                      {new Date(r.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div style={{ fontSize: '.74rem', color: 'var(--text-muted)' }}>
                    {r.mobile}{r.source ? ` · ${r.source}` : ''}{r.deliveryStatus ? ` · ${r.deliveryStatus}` : ''}{r.retryCount ? ` · retried ${r.retryCount}×` : ''}
                  </div>
                  {r.status === 'failed' && <div style={{ fontSize: '.78rem', color: 'var(--danger, #dc2626)', overflowWrap: 'anywhere' }}>{r.error || 'Failed'}</div>}
                  <div className={isOpen ? '' : 'mlog-clamp'} style={{ fontSize: '.8rem', color: 'var(--text-muted)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.body}</div>
                  {isOpen && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); copy(r.body); }}><Copy size={13} style={{ marginRight: 4 }} />Copy text</button>
                      {r.providerMessageId && <span style={{ fontSize: '.7rem', color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>ID {r.providerMessageId}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
